import { useEffect, useState, useCallback } from "react";
import { Banknote, Receipt, Package, UserCheck, FileText, AlertTriangle, TrendingUp, Wallet, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api, { fmtErr, peso, fmtDateTime } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { NeedTenant } from "../components/layout/AppShell";

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6" data-testid={testid}>
      <div className="flex items-center gap-2 text-slate-500">
        <Icon className="h-4 w-4" />
        <span className="text-xs font-semibold uppercase tracking-wider">{label}</span>
      </div>
      <p className="font-mono font-bold text-2xl tracking-tight text-slate-950 mt-2">{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </div>
  );
}

export default function DashboardPage() {
  const { user, activeTenantId, subscription } = useAuth();
  const [data, setData] = useState(null);
  const [report, setReport] = useState(null);
  const [bk, setBk] = useState(null);
  const [birStatus, setBirStatus] = useState(null);
  const [secAlerts, setSecAlerts] = useState([]);
  const [expForm, setExpForm] = useState({ amount: "", category: "Supplies", note: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/summary");
      setData(data);
      api.get("/dashboard/bookkeeping").then(({ data: b }) => setBk(b)).catch(() => {});
      if (data.bir_enabled) {
        api.get("/bir/status").then(({ data: s }) => setBirStatus(s)).catch(() => {});
      }
      api.get("/security/alerts").then(({ data: a }) => setSecAlerts(a.alerts || [])).catch(() => {});
    } catch (e) { toast.error(fmtErr(e)); }
  }, []);
  useEffect(() => {
    if (user.role === "system_admin" && !activeTenantId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, activeTenantId]);

  const genReport = async (type) => {
    try {
      const { data } = await api.get(`/reports/${type}`);
      setReport(data);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  if (user.role === "system_admin" && !activeTenantId) return <NeedTenant />;
  if (!data) return <div className="py-20 text-center text-sm text-slate-400">Loading dashboard…</div>;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Executive Dashboard</h1>
          <p className="text-sm text-slate-500">Live telemetry across your business</p>
        </div>
        <div className="flex-1" />
        {data.bir_enabled && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => genReport("x")} data-testid="dashboard-generate-x-report-btn">
              <FileText className="h-4 w-4 mr-1.5" /> X-Report
            </Button>
            <Button variant="outline" onClick={() => genReport("z")} data-testid="dashboard-generate-z-report-btn">
              <FileText className="h-4 w-4 mr-1.5" /> Z-Report
            </Button>
          </div>
        )}
      </div>

      {subscription && !subscription.locked && subscription.days_remaining !== null
        && subscription.days_remaining <= (subscription.plan_id === "free" ? 2 : 7) && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="dashboard-subscription-reminder">
          {subscription.plan_id === "free" ? "Free trial" : "Subscription"} expires in {subscription.days_remaining} day{subscription.days_remaining === 1 ? "" : "s"}.{" "}
          <Link to="/subscription" className="font-semibold underline">Renew now</Link>
        </div>
      )}
      {birStatus && !birStatus.complete && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 flex flex-wrap items-center gap-3" data-testid="dashboard-bir-incomplete-banner">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <p className="text-sm text-amber-800 flex-1">
            BIR Compliance Center: <span className="font-semibold">incomplete</span> — {birStatus.checks.filter((c) => !c.ok).map((c) => c.label).join(", ")} missing.
          </p>
          <Link to="/bir"><Button size="sm" className="bg-slate-900" data-testid="dashboard-bir-complete-btn">Complete Configuration</Button></Link>
        </div>
      )}
      {secAlerts.length > 0 && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 space-y-1.5" data-testid="dashboard-security-alerts">
          <p className="text-sm font-semibold text-red-800 flex items-center gap-2"><ShieldAlert className="h-4 w-4" /> Security &amp; fraud alerts</p>
          {secAlerts.map((a, i) => (
            <p key={i} className="text-xs text-red-700">
              <span className="font-bold uppercase">[{a.severity}]</span> {a.message}
            </p>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Banknote} label="Today's Sales" value={peso(data.today_sales)} sub={`${data.today_transactions} transactions`} testid="metric-today-sales" />
        <StatCard icon={Receipt} label="Transactions" value={data.today_transactions} sub="today" testid="metric-transactions-count" />
        <StatCard icon={Package} label="Inventory Value" value={peso(data.inventory_valuation)} sub="at cost" testid="metric-inventory-valuation" />
        <StatCard icon={UserCheck} label="Active Staff" value={data.active_staff_count} sub="on shift now" testid="metric-active-staff" />
      </div>

      {bk && (
        <div className="space-y-4" data-testid="bookkeeping-section">
          <h2 className="font-heading text-lg font-semibold tracking-tight text-slate-900">Bookkeeping — {bk.month}</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={Banknote} label="Gross Sales" value={peso(bk.gross_sales)} sub={`${bk.transaction_count} paid invoices`} testid="metric-gross-sales" />
            <StatCard icon={Receipt} label="Net Sales" value={peso(bk.net_sales)} sub={`after ${peso(bk.discounts)} discounts`} testid="metric-net-sales" />
            <StatCard icon={Package} label="COGS" value={peso(bk.cogs)} sub="product cost of items sold" testid="metric-cogs" />
            <StatCard icon={TrendingUp} label="Gross Profit" value={peso(bk.gross_profit)} sub="net sales less cost" testid="metric-gross-profit" />
            <StatCard icon={Wallet} label="Expenditures" value={peso(bk.expenses_total)} sub="recorded expenses" testid="metric-expenses" />
            <StatCard icon={TrendingUp} label="Net Income" value={peso(bk.net_income)} sub="gross profit less expenses" testid="metric-net-income" />
            <StatCard icon={Package} label="Inventory Assets" value={peso(bk.inventory_assets)} sub="on-hand stock at cost" testid="metric-inventory-assets" />
            <StatCard icon={Receipt} label="Voids / Refunds" value={`${bk.voided_count} / ${bk.refunded_count}`} sub="this month" testid="metric-voids-refunds" />
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
              <h3 className="font-heading font-semibold text-slate-900">Record Expenditure</h3>
              <form className="flex flex-wrap items-end gap-3" onSubmit={async (e) => {
                e.preventDefault();
                try {
                  await api.post("/expenses", { amount: Number(expForm.amount), category: expForm.category, note: expForm.note || null });
                  toast.success("Expense recorded");
                  setExpForm({ amount: "", category: "Supplies", note: "" });
                  load();
                } catch (err) { toast.error(fmtErr(err)); }
              }}>
                <div className="space-y-1 w-28">
                  <p className="text-xs text-slate-500">Amount</p>
                  <Input type="number" step="0.01" min="0" required value={expForm.amount} onChange={(e) => setExpForm({ ...expForm, amount: e.target.value })} data-testid="expense-amount-input" />
                </div>
                <div className="space-y-1 w-36">
                  <p className="text-xs text-slate-500">Category</p>
                  <Input value={expForm.category} onChange={(e) => setExpForm({ ...expForm, category: e.target.value })} data-testid="expense-category-input" />
                </div>
                <div className="space-y-1 flex-1 min-w-32">
                  <p className="text-xs text-slate-500">Note</p>
                  <Input value={expForm.note} onChange={(e) => setExpForm({ ...expForm, note: e.target.value })} data-testid="expense-note-input" />
                </div>
                <Button type="submit" className="bg-slate-900" data-testid="expense-add-btn">Add</Button>
              </form>
              <div className="space-y-2">
                {bk.recent_expenses.map((x) => (
                  <div key={x.expense_id} className="flex items-center justify-between text-sm border-b border-slate-50 pb-2 last:border-0">
                    <div>
                      <p className="text-slate-700">{x.category}{x.note ? ` — ${x.note}` : ""}</p>
                      <p className="text-xs text-slate-400">{fmtDateTime(x.spent_at)}</p>
                    </div>
                    <span className="font-mono font-semibold text-slate-900">{peso(x.amount)}</span>
                  </div>
                ))}
                {bk.recent_expenses.length === 0 && <p className="text-sm text-slate-400">No expenses this month.</p>}
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
              <h3 className="font-heading font-semibold text-slate-900">Top Products — This Month</h3>
              {bk.top_products.map((p) => (
                <div key={p.name} className="flex items-center justify-between text-sm border-b border-slate-50 pb-2 last:border-0">
                  <span className="text-slate-700">{p.name} <span className="text-xs text-slate-400">×{p.qty}</span></span>
                  <span className="font-mono font-semibold text-slate-900">{peso(p.revenue)}</span>
                </div>
              ))}
              {bk.top_products.length === 0 && <p className="text-sm text-slate-400">No sales this month.</p>}
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-5 sm:p-6">
          <h3 className="font-heading font-semibold text-slate-900 mb-4">Sales — Last 7 Days</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.sales_trend} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                <defs>
                  <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#059669" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#059669" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(d) => d.slice(5)} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} width={70}
                  tickFormatter={(v) => "₱" + (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v)} />
                <Tooltip formatter={(v) => [peso(v), "Sales"]} contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #E2E8F0" }} />
                <Area type="monotone" dataKey="total" stroke="#059669" strokeWidth={2} fill="url(#salesGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6 space-y-4" data-testid="dashboard-branch-performance">
          <h3 className="font-heading font-semibold text-slate-900">Branch Performance — Today</h3>
          <div className="space-y-3">
            {data.branch_performance.map((b) => (
              <div key={b.branch_id} className="flex items-center justify-between border-b border-slate-50 pb-3 last:border-0">
                <div>
                  <p className="text-sm font-medium text-slate-900">{b.branch_name}</p>
                  <p className="text-xs text-slate-400">{b.today_count} sales</p>
                </div>
                <span className="font-mono font-semibold text-slate-900">{peso(b.today_total)}</span>
              </div>
            ))}
            {data.branch_performance.length === 0 && <p className="text-sm text-slate-400">No branches.</p>}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6 space-y-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <h3 className="font-heading font-semibold text-slate-900">Stock Alerts</h3>
          </div>
          <div className="flex gap-2 text-xs">
            <span className="rounded bg-amber-100 border border-amber-300 px-2 py-1 font-bold text-amber-700">{data.low_stock_count} low</span>
            <span className="rounded bg-orange-100 border border-orange-200 px-2 py-1 font-bold text-orange-700">{data.expiring_count} expiring</span>
            <span className="rounded bg-red-100 border border-red-200 px-2 py-1 font-bold text-red-700">{data.expired_count} expired</span>
          </div>
          <div className="space-y-2">
            {[
              ...data.low_stock_items.map((p) => ({ ...p, _bucket: "low" })),
              ...data.expiring_items.map((p) => ({ ...p, _bucket: "exp" })),
            ].slice(0, 6).map((p) => (
              <div key={`${p._bucket}-${p.product_id}`} className="flex items-center justify-between text-sm">
                <span className="text-slate-700 truncate">{p.name}</span>
                <span className="font-mono text-xs text-slate-500 shrink-0 ml-2">{p.stock_quantity} left</span>
              </div>
            ))}
            {data.low_stock_items.length + data.expiring_items.length === 0 && (
              <p className="text-sm text-slate-400">All stock levels healthy.</p>
            )}
          </div>
        </div>

        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-5 sm:p-6 space-y-4">
          <h3 className="font-heading font-semibold text-slate-900">Recent Sales — Today</h3>
          <div className="space-y-2">
            {data.recent_sales.map((s) => (
              <div key={s.sale_id} className="flex items-center justify-between border-b border-slate-50 pb-2 last:border-0">
                <div>
                  <p className="font-mono text-xs text-slate-500">{s.invoice_number}</p>
                  <p className="text-xs text-slate-400">{s.cashier_name} · {fmtDateTime(s.created_at)}</p>
                </div>
                <div className="text-right">
                  <p className="font-mono font-semibold text-sm text-slate-900">{peso(s.total_amount)}</p>
                  <p className="text-[10px] uppercase text-slate-400">{s.payment_method}</p>
                </div>
              </div>
            ))}
            {data.recent_sales.length === 0 && <p className="text-sm text-slate-400">No sales yet today.</p>}
          </div>
        </div>
      </div>

      <Dialog open={!!report} onOpenChange={(o) => !o && setReport(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">{report?.report_type}-Report — {report?.date}</DialogTitle></DialogHeader>
          {report && (
            <div className="font-mono text-xs space-y-2" data-testid="bir-report-content">
              <p className="text-slate-500">{report.store_name} · Generated {fmtDateTime(report.generated_at)} by {report.generated_by}</p>
              <div className="border-t border-slate-100 pt-2 space-y-1">
                <div className="flex justify-between"><span>Transactions</span><span>{report.transaction_count}</span></div>
                <div className="flex justify-between"><span>Gross Sales</span><span>{peso(report.gross_sales)}</span></div>
                <div className="flex justify-between"><span>VATable Sales</span><span>{peso(report.vatable_sales)}</span></div>
                <div className="flex justify-between"><span>VAT (12%)</span><span>{peso(report.vat_amount)}</span></div>
                <div className="flex justify-between"><span>Discounts</span><span>{peso(report.total_discounts)}</span></div>
                {Object.entries(report.payments_breakdown).map(([m, v]) => (
                  <div key={m} className="flex justify-between capitalize text-slate-500"><span>{m}</span><span>{peso(v)}</span></div>
                ))}
                <div className="flex justify-between text-slate-500 pt-1"><span>Invoice range</span><span>{report.first_invoice || "—"} → {report.last_invoice || "—"}</span></div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
